import * as React from 'react';
import Banner from '@joinforma/ui/components/Banner';
import { BannerVariant } from '@joinforma/ui/constants/properties';

import {
  ALEGEUS_GROUP_ONE_ACCESS_DATE,
  ALEGEUS_GROUP_TWO_ACCESS_DATE,
  ALEGEUS_HSA_INVESTMENTS_SELL_DEADLINE,
  MemberHsaTransferType,
} from 'constants/hsaBulkTransfer';
import { PretaxHsaBulkTransfer } from 'contexts/PretaxContext/types';
import {
  AlegeusSmallStoreReminderBanner,
  AlegeusSmallStoreTransferBanner,
  AlegeusSmallStoreMissedBanner,
  NonAlegeusSmallStoreTransferBanner,
  NonAlegeusSmallStoreConfirmBanner,
  AlegeusLargeAccountsListTransferBanner,
  NonAlegeusLargeAccountsListTransferBanner,
  AlegeusLargeOldHsaTransferBanner,
  AlegeusLargeNewHsaTransferBanner,
  NonAlegeusLargeNewHsaTransferBanner,
  NonAlegeusSmallAccountsListNewHsaMissedBanner,
  AlegeusLargeAccountsListOldHsaNewHsaTransferBanner,
  AlegeusLargeAccountsListOldHsaNewHsaReminderBanner,
  AlegeusCalloutTransfer,
  AlegeusCalloutReminder,
  AlegeusLargeAccountsListMissedBanner,
  AlegeusLargeAccountsListOldHsaMissedBanner,
  AlegeusLargeOldHsaMissedBanner,
  AlegeusLargeNewHsaMissedBanner,
  AlegeusSmallStoreBanner217V1,
  AlegeusSmallStoreBanner217V2,
  AlegeusLargeAccountsListOldHsaNewHsa217Banner,
  AlegeusLargeAccountsListOldHsa217Banner,
  AlegeusLargeNewHsa217Banner,
  AlegeusCallout217V1,
  AlegeusCallout217V2,
} from 'components/common/HsaBulkTransferComponents';
import dayjs from 'utils/date-helper';

/**
 * Function that returns null or one of the following banners (7)
 *  - AL_SM_ST_TR_V1_BANNER
 *  - AL_SM_ST_RE_V1_BANNER
 *  - AL_SM_ST_MI_V1_BANNER
 *  - AL_SM_ST_217_BANNER
 *  - AL_SM_ST_217_V2_BANNER
 *
 *  - NAL_SM_ST_AL_NH_CO_V1_BANNER
 *  - NAL_SM_ST_TR_V1_BANNER
 */
export const displayHsaBulkTransferBannersOnStore = (
  hsaBulkTransfer?: PretaxHsaBulkTransfer
) => {
  if (!hsaBulkTransfer) {
    return null;
  }

  const isTodayPriorToOptInWindow = dayjs().isBefore(
    hsaBulkTransfer.optInStartDate
  );

  if (isTodayPriorToOptInWindow) {
    return null;
  }

  const isAlegeusCustodian = hsaBulkTransfer.custodianName === 'alegeus';
  const isNonAlegeusCustodian = !isAlegeusCustodian;
  const memberAcceptedTerms =
    hsaBulkTransfer.memberStatus === MemberHsaTransferType.Accept;
  const isWithinOptInWindow = dayjs().isBetween(
    dayjs(hsaBulkTransfer.optInStartDate),
    dayjs(hsaBulkTransfer.transferDeadline),
    'day',
    '[]'
  );
  const isTodayBeforeAlegeusHsaInvestmentsSellDeadline = dayjs().isSameOrBefore(
    ALEGEUS_HSA_INVESTMENTS_SELL_DEADLINE
  );
  const isTodayBeforeFundsTransferDatePlusOneDay = dayjs().isSameOrBefore(
    dayjs(hsaBulkTransfer.fundsTransferDate).add(1, 'day').toDate()
  );
  const isTodayBeforeFundsTransferDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(hsaBulkTransfer.fundsTransferDate).add(7, 'day').toDate()
  );
  const isTodayBeforeGroupOneBlackoutDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(ALEGEUS_GROUP_ONE_ACCESS_DATE).add(7, 'day').toDate()
  );
  const isTodayBeforeGroupTwoBlackoutDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(ALEGEUS_GROUP_TWO_ACCESS_DATE).add(7, 'day').toDate()
  );
  const memberSoldInvestments =
    !hsaBulkTransfer.memberHasAlegeusHsaInvestmentBalance;
  const memberIsGroupOne = !hsaBulkTransfer.isMemberAlegeusGroupTwo;

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    isTodayBeforeAlegeusHsaInvestmentsSellDeadline
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={<AlegeusSmallStoreReminderBanner />}
      />
    );
  }

  if (isAlegeusCustodian && !memberAcceptedTerms && isWithinOptInWindow) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={<AlegeusSmallStoreTransferBanner />}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    !memberAcceptedTerms &&
    !isWithinOptInWindow &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={<AlegeusSmallStoreMissedBanner />}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    memberSoldInvestments &&
    !isWithinOptInWindow &&
    !isTodayBeforeAlegeusHsaInvestmentsSellDeadline &&
    memberIsGroupOne &&
    isTodayBeforeGroupOneBlackoutDatePlusSevenDays
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={
          <AlegeusSmallStoreBanner217V1
            isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
          />
        }
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    memberSoldInvestments &&
    !isWithinOptInWindow &&
    !isTodayBeforeAlegeusHsaInvestmentsSellDeadline &&
    !memberIsGroupOne &&
    isTodayBeforeGroupTwoBlackoutDatePlusSevenDays
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={
          <AlegeusSmallStoreBanner217V1
            isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
          />
        }
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    !memberSoldInvestments &&
    !isWithinOptInWindow &&
    !isTodayBeforeAlegeusHsaInvestmentsSellDeadline &&
    memberIsGroupOne &&
    isTodayBeforeGroupOneBlackoutDatePlusSevenDays
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={<AlegeusSmallStoreBanner217V2 />}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    !memberSoldInvestments &&
    !isWithinOptInWindow &&
    !isTodayBeforeAlegeusHsaInvestmentsSellDeadline &&
    !memberIsGroupOne &&
    isTodayBeforeGroupTwoBlackoutDatePlusSevenDays
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={<AlegeusSmallStoreBanner217V2 />}
      />
    );
  }

  if (
    isNonAlegeusCustodian &&
    memberAcceptedTerms &&
    isTodayBeforeFundsTransferDatePlusOneDay
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={
          <NonAlegeusSmallStoreConfirmBanner
            fundsTransferDate={hsaBulkTransfer.fundsTransferDate}
          />
        }
      />
    );
  }

  if (isNonAlegeusCustodian && !memberAcceptedTerms && isWithinOptInWindow) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={
          <NonAlegeusSmallStoreTransferBanner
            onClick={hsaBulkTransfer.openModal}
          />
        }
      />
    );
  }

  return null;
};

/**
 * Function that returns null or one of the following banners (10)
 *  - AL_LG_AL_TR_V1_BANNER
 *  - AL_LG_AL_OH_NH_TR_V1_BANNER
 *  - AL_LG_AL_OH_NH_RE_V1_BANNER
 *  - AL_LG_AL_MI_V1_BANNER
 *  - AL_LG_AL_OH_MI_V1_BANNER
 *  - AL_LG_AL_OH_NH_217_BANNER
 *  - AL_LG_AL_OH_217_BANNER
 *
 *  - NAL_SM_ST_AL_NH_CO_V1_BANNER
 *  - NAL_SM_AL_NH_MI_V1_BANNER
 *  - NAL_LG_AL_TR_V1_BANNER
 */
export const displayHsaBulkTransferBannersOnAccountsListPage = (
  hsaBulkTransfer?: PretaxHsaBulkTransfer
) => {
  if (!hsaBulkTransfer) {
    return null;
  }

  const isTodayPriorToOptInWindow = dayjs().isBefore(
    hsaBulkTransfer.optInStartDate
  );

  if (isTodayPriorToOptInWindow) {
    return null;
  }

  const isAlegeusCustodian = hsaBulkTransfer.custodianName === 'alegeus';
  const isNonAlegeusCustodian = !isAlegeusCustodian;
  const memberAcceptedTerms =
    hsaBulkTransfer.memberStatus === MemberHsaTransferType.Accept;
  const isWithinOptInWindow = dayjs().isBetween(
    dayjs(hsaBulkTransfer.optInStartDate),
    dayjs(hsaBulkTransfer.transferDeadline),
    'day',
    '[]'
  );
  const isTodayBeforeInvestmentsSellDeadline = dayjs().isSameOrBefore(
    dayjs(ALEGEUS_HSA_INVESTMENTS_SELL_DEADLINE)
  );
  const isTodayBeforeFundsTransferDatePlusOneDay = dayjs().isSameOrBefore(
    dayjs(hsaBulkTransfer.fundsTransferDate).add(1, 'day')
  );
  const isTodayBeforeFundsTransferDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(hsaBulkTransfer.fundsTransferDate).add(7, 'day')
  );
  const isTodayBeforeGroupOneBlackoutDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(ALEGEUS_GROUP_ONE_ACCESS_DATE).add(7, 'day').toDate()
  );
  const isTodayBeforeGroupTwoBlackoutDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(ALEGEUS_GROUP_TWO_ACCESS_DATE).add(7, 'day').toDate()
  );
  const memberSoldInvestments =
    !hsaBulkTransfer.memberHasAlegeusHsaInvestmentBalance;
  const memberIsGroupOne = !hsaBulkTransfer.isMemberAlegeusGroupTwo;

  if (
    isAlegeusCustodian &&
    isWithinOptInWindow &&
    !memberAcceptedTerms &&
    hsaBulkTransfer.memberHasFormaHSA
  ) {
    return (
      <AlegeusLargeAccountsListTransferBanner
        optInDeadline={hsaBulkTransfer.transferDeadline}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    isWithinOptInWindow &&
    !memberAcceptedTerms &&
    !hsaBulkTransfer.memberHasFormaHSA
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaNewHsaTransferBanner
        optInDeadline={hsaBulkTransfer.transferDeadline}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    isTodayBeforeInvestmentsSellDeadline
  ) {
    return <AlegeusLargeAccountsListOldHsaNewHsaReminderBanner />;
  }

  if (
    isAlegeusCustodian &&
    !memberAcceptedTerms &&
    !isWithinOptInWindow &&
    isTodayBeforeFundsTransferDatePlusSevenDays &&
    hsaBulkTransfer.memberHasFormaHSA
  ) {
    return (
      <AlegeusLargeAccountsListMissedBanner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    !memberAcceptedTerms &&
    !isWithinOptInWindow &&
    isTodayBeforeFundsTransferDatePlusSevenDays &&
    !hsaBulkTransfer.memberHasFormaHSA
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaMissedBanner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    memberSoldInvestments &&
    memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupOneBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaNewHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
        alegeusAccountId={hsaBulkTransfer.alegeusHsaAccountId}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    memberSoldInvestments &&
    !memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupTwoBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaNewHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
        alegeusAccountId={hsaBulkTransfer.alegeusHsaAccountId}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    !memberSoldInvestments &&
    memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupOneBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    !memberSoldInvestments &&
    !memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupTwoBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
      />
    );
  }

  if (
    isNonAlegeusCustodian &&
    memberAcceptedTerms &&
    isTodayBeforeFundsTransferDatePlusOneDay
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={
          <NonAlegeusSmallStoreConfirmBanner
            fundsTransferDate={hsaBulkTransfer.fundsTransferDate}
          />
        }
      />
    );
  }

  if (isNonAlegeusCustodian && !memberAcceptedTerms && isWithinOptInWindow) {
    return (
      <NonAlegeusLargeAccountsListTransferBanner
        optInDeadline={hsaBulkTransfer.transferDeadline}
        onClick={hsaBulkTransfer.openModal}
      />
    );
  }

  if (
    isNonAlegeusCustodian &&
    !memberAcceptedTerms &&
    !isWithinOptInWindow &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={<NonAlegeusSmallAccountsListNewHsaMissedBanner />}
      />
    );
  }

  return null;
};

/**
 * Function that returns null or one of the following banners (7)
 *  - AL_LG_OH_TR_V1_BANNER
 *  - AL_LG_AL_OH_NH_TR_V1_BANNER
 *  - AL_LG_AL_OH_NH_RE_V1_BANNER
 *  - AL_LG_OH_MI_V1_BANNER
 *  - AL_LG_AL_OH_MI_V1_BANNER
 *  - AL_LG_AL_OH_NH_217_BANNER
 *  - AL_LG_AL_OH_217_BANNER
 */
export const displayHsaBulkTransferBannersOnAlegeusHsaDetailPage = (
  hsaBulkTransfer?: PretaxHsaBulkTransfer
) => {
  if (!hsaBulkTransfer) {
    return null;
  }

  const isTodayPriorToOptInWindow = dayjs().isBefore(
    hsaBulkTransfer.optInStartDate
  );

  if (isTodayPriorToOptInWindow) {
    return null;
  }

  const isWithinOptInWindow = dayjs().isBetween(
    dayjs(hsaBulkTransfer.optInStartDate),
    dayjs(hsaBulkTransfer.transferDeadline),
    'day',
    '[]'
  );

  const memberAcceptedTerms =
    hsaBulkTransfer.memberStatus === MemberHsaTransferType.Accept;

  const isTodayBeforeInvestmentsSellDeadline = dayjs().isSameOrBefore(
    ALEGEUS_HSA_INVESTMENTS_SELL_DEADLINE
  );
  const isTodayBeforeFundsTransferDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(hsaBulkTransfer.fundsTransferDate).add(7, 'day')
  );
  const isTodayBeforeGroupOneBlackoutDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(ALEGEUS_GROUP_ONE_ACCESS_DATE).add(7, 'day').toDate()
  );
  const isTodayBeforeGroupTwoBlackoutDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(ALEGEUS_GROUP_TWO_ACCESS_DATE).add(7, 'day').toDate()
  );
  const memberSoldInvestments =
    !hsaBulkTransfer.memberHasAlegeusHsaInvestmentBalance;
  const memberIsGroupOne = !hsaBulkTransfer.isMemberAlegeusGroupTwo;

  if (
    isWithinOptInWindow &&
    !memberAcceptedTerms &&
    hsaBulkTransfer.memberHasFormaHSA
  ) {
    return (
      <AlegeusLargeOldHsaTransferBanner
        optInDeadline={hsaBulkTransfer.transferDeadline}
      />
    );
  }

  if (
    isWithinOptInWindow &&
    !memberAcceptedTerms &&
    !hsaBulkTransfer.memberHasFormaHSA
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaNewHsaTransferBanner
        optInDeadline={hsaBulkTransfer.transferDeadline}
      />
    );
  }

  if (memberAcceptedTerms && isTodayBeforeInvestmentsSellDeadline) {
    return <AlegeusLargeAccountsListOldHsaNewHsaReminderBanner />;
  }

  if (
    !isWithinOptInWindow &&
    !memberAcceptedTerms &&
    isTodayBeforeFundsTransferDatePlusSevenDays &&
    hsaBulkTransfer.memberHasFormaHSA
  ) {
    return (
      <AlegeusLargeOldHsaMissedBanner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
      />
    );
  }

  if (
    !isWithinOptInWindow &&
    !memberAcceptedTerms &&
    isTodayBeforeFundsTransferDatePlusSevenDays &&
    !hsaBulkTransfer.memberHasFormaHSA
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaMissedBanner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
      />
    );
  }

  if (
    memberAcceptedTerms &&
    memberSoldInvestments &&
    memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupOneBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaNewHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
        alegeusAccountId={hsaBulkTransfer.alegeusHsaAccountId}
      />
    );
  }

  if (
    memberAcceptedTerms &&
    memberSoldInvestments &&
    !memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupTwoBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaNewHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
        alegeusAccountId={hsaBulkTransfer.alegeusHsaAccountId}
      />
    );
  }

  if (
    memberAcceptedTerms &&
    !memberSoldInvestments &&
    memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupOneBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
      />
    );
  }

  if (
    memberAcceptedTerms &&
    !memberSoldInvestments &&
    !memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupTwoBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
      />
    );
  }
};

/**
 * Function that returns null or one of the following banners (9)
 *  - AL_LG_NH_TR_V1_BANNER
 *  - AL_LG_AL_OH_NH_TR_V1_BANNER
 *  - AL_LG_AL_OH_NH_RE_V1_BANNER
 *  - AL_LG_NH_MI_V1_BANNER
 *  - AL_LG_AL_OH_NH_217_BANNER
 *  - AL_LG_NH_217_BANNER
 *
 *  - NAL_SM_ST_AL_NH_CO_V1_BANNER
 *  - NAL_SM_AL_NH_MI_V1_BANNER
 *  - NAL_LG_NH_TR_V1_BANNER
 */
export const displayHsaBulkTransferBannersOnFormaHsaDetailPage = (
  hsaBulkTransfer?: PretaxHsaBulkTransfer
) => {
  if (!hsaBulkTransfer) {
    return null;
  }

  const isTodayPriorToOptInWindow = dayjs().isBefore(
    hsaBulkTransfer.optInStartDate
  );

  if (isTodayPriorToOptInWindow) {
    return null;
  }

  const isAlegeusCustodian = hsaBulkTransfer.custodianName === 'alegeus';
  const isNonAlegeusCustodian = !isAlegeusCustodian;
  const memberAcceptedTerms =
    hsaBulkTransfer.memberStatus === MemberHsaTransferType.Accept;
  const isTodayBeforeInvestmentsSellDeadline = dayjs().isSameOrBefore(
    ALEGEUS_HSA_INVESTMENTS_SELL_DEADLINE
  );
  const isWithinOptInWindow = dayjs().isBetween(
    dayjs(hsaBulkTransfer.optInStartDate),
    dayjs(hsaBulkTransfer.transferDeadline),
    'day',
    '[]'
  );
  const isTodayBeforeFundsTransferDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(hsaBulkTransfer.fundsTransferDate).add(7, 'day')
  );
  const isTodayBeforeFundsTransferDatePlusOneDay = dayjs().isSameOrBefore(
    dayjs(hsaBulkTransfer.fundsTransferDate).add(1, 'day')
  );
  const isTodayBeforeAlegeusHsaInvestmentsSellDeadline = dayjs().isSameOrBefore(
    ALEGEUS_HSA_INVESTMENTS_SELL_DEADLINE
  );
  const isTodayBeforeGroupOneBlackoutDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(ALEGEUS_GROUP_ONE_ACCESS_DATE).add(7, 'day').toDate()
  );
  const isTodayBeforeGroupTwoBlackoutDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(ALEGEUS_GROUP_TWO_ACCESS_DATE).add(7, 'day').toDate()
  );
  const memberSoldInvestments =
    !hsaBulkTransfer.memberHasAlegeusHsaInvestmentBalance;
  const memberIsGroupOne = !hsaBulkTransfer.isMemberAlegeusGroupTwo;

  if (
    isAlegeusCustodian &&
    isWithinOptInWindow &&
    !memberAcceptedTerms &&
    hsaBulkTransfer.memberHasFormaHSA
  ) {
    return (
      <AlegeusLargeNewHsaTransferBanner
        optInDeadline={hsaBulkTransfer.transferDeadline}
      />
    );
  }

  if (
    isWithinOptInWindow &&
    !memberAcceptedTerms &&
    !hsaBulkTransfer.memberHasFormaHSA
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaNewHsaTransferBanner
        optInDeadline={hsaBulkTransfer.transferDeadline}
      />
    );
  }

  if (isNonAlegeusCustodian && !memberAcceptedTerms && isWithinOptInWindow) {
    return (
      <NonAlegeusLargeNewHsaTransferBanner
        optInDeadline={hsaBulkTransfer.transferDeadline}
        onClick={hsaBulkTransfer.openModal}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    isTodayBeforeAlegeusHsaInvestmentsSellDeadline
  ) {
    return <AlegeusLargeAccountsListOldHsaNewHsaReminderBanner />;
  }

  if (
    isAlegeusCustodian &&
    !memberAcceptedTerms &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeNewHsaMissedBanner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
        alegeusAccountId={hsaBulkTransfer.alegeusHsaAccountId}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    memberSoldInvestments &&
    memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupOneBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaNewHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
        alegeusAccountId={hsaBulkTransfer.alegeusHsaAccountId}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    memberSoldInvestments &&
    !memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupTwoBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeAccountsListOldHsaNewHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
        alegeusAccountId={hsaBulkTransfer.alegeusHsaAccountId}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    !memberSoldInvestments &&
    memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupOneBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeNewHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
        alegeusAccountId={hsaBulkTransfer.alegeusHsaAccountId}
      />
    );
  }

  if (
    isAlegeusCustodian &&
    memberAcceptedTerms &&
    !memberSoldInvestments &&
    !memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupTwoBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusLargeNewHsa217Banner
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
        alegeusAccountId={hsaBulkTransfer.alegeusHsaAccountId}
      />
    );
  }

  if (
    isNonAlegeusCustodian &&
    memberAcceptedTerms &&
    isTodayBeforeFundsTransferDatePlusOneDay
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={
          <NonAlegeusSmallStoreConfirmBanner
            fundsTransferDate={hsaBulkTransfer.fundsTransferDate}
          />
        }
      />
    );
  }

  if (
    isNonAlegeusCustodian &&
    !memberAcceptedTerms &&
    !isWithinOptInWindow &&
    isTodayBeforeFundsTransferDatePlusSevenDays
  ) {
    return (
      <Banner
        variant={BannerVariant.WARNING}
        content={<NonAlegeusSmallAccountsListNewHsaMissedBanner />}
      />
    );
  }

  return null;
};

/**
 * Function that returns null or one of the following banners (4)
 *  - AL_CALLOUT_TR
 *  - AL_CALLOUT_RE
 *  - AL_CALLOUT_217_V1
 *  - AL_CALLOUT_217_V2
 */
export const displayCalloutOnAlegeusHsaCard = (
  hsaBulkTransfer?: PretaxHsaBulkTransfer
) => {
  if (!hsaBulkTransfer) {
    return null;
  }

  const memberAcceptedTerms =
    hsaBulkTransfer.memberStatus === MemberHsaTransferType.Accept;

  const isTodayPriorToOptInWindow = dayjs().isBefore(
    hsaBulkTransfer.optInStartDate
  );

  if (isTodayPriorToOptInWindow) {
    return null;
  }

  const isTodayBeforeInvestmentsSellDeadline = dayjs().isSameOrBefore(
    ALEGEUS_HSA_INVESTMENTS_SELL_DEADLINE
  );
  const isWithinOptInWindow = dayjs().isBetween(
    dayjs(hsaBulkTransfer.optInStartDate),
    dayjs(hsaBulkTransfer.transferDeadline),
    'day',
    '[]'
  );

  const isTodayBeforeAlegeusHsaInvestmentsSellDeadline = dayjs().isSameOrBefore(
    ALEGEUS_HSA_INVESTMENTS_SELL_DEADLINE
  );
  const isTodayBeforeGroupOneBlackoutDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(ALEGEUS_GROUP_ONE_ACCESS_DATE).add(7, 'day').toDate()
  );
  const isTodayBeforeGroupTwoBlackoutDatePlusSevenDays = dayjs().isSameOrBefore(
    dayjs(ALEGEUS_GROUP_TWO_ACCESS_DATE).add(7, 'day').toDate()
  );
  const memberSoldInvestments =
    !hsaBulkTransfer.memberHasAlegeusHsaInvestmentBalance;
  const memberIsGroupOne = !hsaBulkTransfer.isMemberAlegeusGroupTwo;

  if (isWithinOptInWindow && !memberAcceptedTerms) {
    return (
      <AlegeusCalloutTransfer
        optInDeadline={hsaBulkTransfer.transferDeadline}
      />
    );
  }

  if (
    memberAcceptedTerms &&
    isTodayBeforeAlegeusHsaInvestmentsSellDeadline &&
    // only show member has alegeus hsa investment balance
    hsaBulkTransfer.memberHasAlegeusHsaInvestmentBalance
  ) {
    return <AlegeusCalloutReminder />;
  }

  if (
    memberAcceptedTerms &&
    memberSoldInvestments &&
    memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupOneBlackoutDatePlusSevenDays
  ) {
    return <AlegeusCallout217V1 />;
  }

  if (
    memberAcceptedTerms &&
    memberSoldInvestments &&
    !memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupTwoBlackoutDatePlusSevenDays
  ) {
    return <AlegeusCallout217V1 />;
  }

  if (
    memberAcceptedTerms &&
    !memberSoldInvestments &&
    memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupOneBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusCallout217V2
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
      />
    );
  }

  if (
    memberAcceptedTerms &&
    !memberSoldInvestments &&
    !memberIsGroupOne &&
    !isWithinOptInWindow &&
    !isTodayBeforeInvestmentsSellDeadline &&
    isTodayBeforeGroupTwoBlackoutDatePlusSevenDays
  ) {
    return (
      <AlegeusCallout217V2
        isMemberAlegeusGroupTwo={hsaBulkTransfer.isMemberAlegeusGroupTwo}
      />
    );
  }

  return null;
};
